/**
 * dm data service
 */
import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpParams,
} from '@angular/common/http';
import { NgRedux } from '@angular-redux/store';
import { chunk, concat, get, isEqual, head } from 'lodash';

import { IAppState } from '@app/store';
import { Observable, observableOf, observableThrowError, rx } from '@shared/rx';
import {
  IApiListResponse,
  IBaseEntityType,
  IErrorOverride,
  IEventType,
  IFilterQuery,
  EHeaderName,
  ENTITY_TYPES,
  ObjectId,
} from '@shared/types';
import { environment } from '../../../../environments/environment';
import { HttpErrorHandler } from '@shared/services/base/http-error-handler';
import { Preferences } from '@geniuspos/xenial-data-contracts/types/dm-entities';
import { HttpErrorService } from '../http-error';
import { NO_PERMISSION_STATUS } from '@app/shared/constants';
import { batchRequestMap } from '@shared/rx-operators';
import { SITE_GROUP_BATCH_SIZE } from '@config/global-constants';
import {
  buildServiceIdentifyContext,
  generateObjectId,
  toMoment,
} from '@app/shared/utils';
import { UiActions } from '@app/store/actions';
import { XVII18nService } from '@app/i18n/services/xvi-translate.service';

@Injectable()
export class DmDataService {
  public static UTILITY_TYPE: string = 'DmDataService';
  public defaultQuery: IFilterQuery = {};

  private get baseUrl(): string {
    return environment.dmBackendURI;
  }

  // eslint-disable-next-line max-params
  public constructor(
    private http: HttpClient,
    public ngRedux: NgRedux<IAppState>,
    public httpErrorService: HttpErrorService,
    public uiActions: UiActions,
    public i18nService: XVII18nService,
  ) {}

  public getEventTypes(): Observable<IEventType[]> {
    return this.http
      .get(`${this.baseUrl}/event-type/master`, {
        context: buildServiceIdentifyContext(ENTITY_TYPES.EVENT_TYPE),
      })
      .pipe(
        this.catchHttpErrorAndPickErrorList(),
        rx.map((response: IApiListResponse<IEventType>) =>
          get(response, 'items', []),
        ),
      );
  }

  public getSiteGroupId(siteIds: string[]): Observable<string> {
    return this.http
      .post(`${this.baseUrl}/site-group`, siteIds)
      .pipe(this.catchHttpErrorAndPickErrorList());
  }

  public getSiteGroupIdList(
    siteIds: string[],
    batchSize = SITE_GROUP_BATCH_SIZE,
  ): Observable<string[]> {
    return batchRequestMap(chunk(siteIds, batchSize), (siteId: string[]) =>
      this.getSiteGroupId(siteId),
    ).pipe(rx.reduce((groupIds, groupId) => concat(groupIds, groupId), []));
  }

  public getSitesPreferencesBySiteGroupId(
    siteGroupId: string,
  ): Observable<IBaseEntityType> {
    return this.http
      .get(`${this.baseUrl}/preferences/site?prefix=back_office`, {
        headers: {
          'x-site-group-id': siteGroupId,
        },
      })
      .pipe(this.catchHttpErrorAndPickErrorList());
  }

  public getSitesPreferencesBySiteId(
    siteId: string,
  ): Observable<IBaseEntityType> {
    return this.http
      .get(`${this.baseUrl}/preferences/site?prefix=back_office`, {
        headers: {
          [EHeaderName.XSiteIds]: siteId,
        },
      })
      .pipe(
        rx.map((sitePreferences: IBaseEntityType[]) => head(sitePreferences)),
        this.catchHttpErrorAndPickErrorList(),
      );
  }

  public bulkUpdateDMEntity(
    entityType: ENTITY_TYPES,
    items: IBaseEntityType[],
    siteId: ObjectId,
  ): Observable<IBaseEntityType> {
    return this.http
      .post(
        `${this.baseUrl}/${entityType}/dependent-bulk-update`,
        {
          items,
        },
        {
          headers: {
            [EHeaderName.XSiteIds]: siteId,
            [EHeaderName.XCalculateEffectiveDate]: 'true',
            [EHeaderName.XCartEntityId]: generateObjectId(),
            [EHeaderName.XPathWithBusinessDate]: toMoment().utc().toISOString(),
          },
          context: buildServiceIdentifyContext(entityType),
        },
      )
      .pipe(this.catchHttpThrowError({ showErrorMessage: true }));
  }

  /**
   * Get preferences for the current company use user token.
   *
   * @param query FilterQuery
   */
  public fetchPreferencesByUserToken(
    query?: IFilterQuery,
  ): Observable<Preferences> {
    let params = new HttpParams();
    if (query && 'prefix' in query) {
      params = params.set('$filter', query['filter']);
    }

    return this.http
      .get(`${this.baseUrl}/preferences/company`, { params })
      .pipe(this.catchHttpError());
  }

  /* eslint-disable  */
  protected catchHttpError =
    (errorOverride?: IErrorOverride) =>
    <T>(input$: Observable<T>): Observable<T> =>
      input$.pipe(
        rx.catchError((httpError: HttpErrorResponse) => {
          return observableThrowError(
            HttpErrorHandler.composeError(httpError, {
              errorOverride,
              i18nService: this.i18nService,
            }),
          );
        }),
      );

  /* eslint-disable */
  protected catchHttpThrowError =
    (options?: {
      throwError?: boolean;
      showErrorMessage?: boolean;
      isBrokenWhenNoPermission?: boolean;
    }) =>
    <T>(input$: Observable<T>): Observable<T> =>
      input$.pipe(
        rx.catchError(({ error }: HttpErrorResponse) => {
          const {
            throwError = false,
            showErrorMessage = false,
            isBrokenWhenNoPermission = false,
          } = options || {};
          if (isEqual(error.status, NO_PERMISSION_STATUS)) {
            return this.httpErrorService
              .catchNoPermissionHttpError([error], { isBrokenWhenNoPermission })
              .pipe(
                rx.switchMap((result) => {
                  if (throwError) {
                    return observableThrowError(error);
                  }

                  return observableOf(result);
                }),
              );
          }

          if (showErrorMessage) {
            const toast = {
              errors: {
                title: error.title,
                message: error?.meta?.message || '',
              },
            };

            this.uiActions.showErrorToasterMessage(toast);
          }

          return observableThrowError(error);
        }),
      );

  /* eslint-disable */
  private catchHttpErrorAndPickErrorList =
    (): any =>
    <T>(input$: Observable<T>): Observable<T> =>
      input$.pipe(
        rx.catchError(({ error }: HttpErrorResponse) => {
          if (isEqual(error.status, NO_PERMISSION_STATUS)) {
            return this.httpErrorService.catchNoPermissionHttpError([error]);
          }

          return observableThrowError(get(error, 'errors'));
        }),
      );
}
